/**
 * Error Boundary for Chat Components
 * Catches and handles errors in chat-related components
 */

import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { 
  Alert, 
  Button, 
  Center, 
  Group, 
  Stack, 
  Text, 
  Title 
} from '@mantine/core';
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ChatErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ChatErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      errorInfo,
    });

    // Call optional error callback
    this.props.onError?.(error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <Center h="100%" p="xl">
          <Stack align="center" gap="lg" maw={500}>
            <IconAlertTriangle size={48} color="orange" />
            
            <Title order={3} ta="center">
              Something went wrong
            </Title>
            
            <Text ta="center" c="dimmed">
              We encountered an error while loading the chat interface. 
              This could be due to a temporary issue or a problem with your browser's local storage.
            </Text>

            {this.state.error && (
              <Alert 
                icon={<IconAlertTriangle size={16} />} 
                title="Error Details" 
                color="orange"
                variant="light"
                style={{ width: '100%' }}
              >
                <Text size="sm" ff="monospace">
                  {this.state.error.message}
                </Text>
              </Alert>
            )}

            <Group>
              <Button 
                leftSection={<IconRefresh size={16} />}
                onClick={this.handleReset}
                variant="light"
              >
                Try Again
              </Button>
              
              <Button 
                onClick={this.handleReload}
                color="orange"
              >
                Reload Page
              </Button>
            </Group>

            <Text size="sm" c="dimmed" ta="center">
              If this problem persists, try clearing your browser cache or 
              contact support if you continue to experience issues.
            </Text>
          </Stack>
        </Center>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for easier usage
export function withChatErrorBoundary<T extends object>(
  Component: React.ComponentType<T>,
  fallback?: ReactNode
) {
  const WrappedComponent = (props: T) => (
    <ChatErrorBoundary fallback={fallback}>
      <Component {...props} />
    </ChatErrorBoundary>
  );

  WrappedComponent.displayName = `withChatErrorBoundary(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
}